import { htmlCanvas, widget } from "@foretagsplatsen/widgetjs";
import { timesIcon } from "../../elements/icons/icons.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import jQuery from "jquery";
import responsive from "../../responsive.js";
import stacker from "../../elements/stacker/stacker.js";
import text from "../../elements/text/text.js";

const configuration = {
	delay: 6000,
};

/**
 * Configuration for `notification`.
 *
 * @param {number} [delay] - Number of milliseconds the notification
 * will stay visible.
 */
export function setConfiguration({ delay } = {}) {
	configuration.delay = delay;
}

/**
 * `Notifier` appends contents to a single `#alertarea` DOM element.
 */
export class Notifier {
	constructor() {
		this._alertBusUnbindFunctions = [];
	}

	success(message, options) {
		this._alert({ message, type: "success", ...options });
	}

	warning(message, options) {
		this._alert({ message, type: "warning", ...options });
	}

	error(message, options) {
		this._alert({ message, type: "danger", ...options });
	}

	inform(message, options) {
		this._alert({ message, type: "info", ...options });
	}

	notifyOnAlerts({ alertBus }) {
		this.stopNotifyingOnAlerts();

		this._alertBusUnbindFunctions = [
			alertBus.onSuccess((alert) =>
				this.success(...this._convertAlertModel(alert)),
			),
			alertBus.onWarning((alert) =>
				this.warning(...this._convertAlertModel(alert)),
			),
			alertBus.onError((alert) =>
				this.error(...this._convertAlertModel(alert)),
			),
		];
	}

	stopNotifyingOnAlerts() {
		this._alertBusUnbindFunctions.forEach((unbind) => unbind());
		this._alertBusUnbindFunctions = [];
	}

	_alert({
		message,
		type,
		buttons = [],
		manualClose = false,
		onClose = () => {},
	}) {
		const cssClass = type;
		let id = "#alertarea";

		if (responsive.isMobileOrIPadPortaitBreakpoint()) {
			id += "-mobile";
		}

		const div = jQuery(id);
		if (!div.get(0)) {
			return;
		}

		htmlCanvas(div).render((html) => {
			const alert = html.div(
				{
					klass: [
						"administrate-alert-zone",
						"alert",
						`alert-${cssClass}`,
					],
					style: "display : none",
				},
				stacker(
					{ spacing: "between" },
					text(message),
					stacker(
						...buttons,
						button(
							{
								link: true,
								class: "close-alert",
								dataTest: "close-alert",
								onClick: () => {
									this._closeAlert(alert);
									onClose();
								},
							},
							timesIcon,
						),
					),
				),
			);

			if (!manualClose) {
				setTimeout(() => {
					this._closeAlert(alert);
				}, configuration.delay);
			}

			this._showAlert(alert);
		});
	}

	_showAlert(alert) {
		alert.asJQuery().slideDown(300);
	}

	_closeAlert(alert) {
		alert.asJQuery().slideUp(300, () => {
			alert.asJQuery().remove();
		});
	}

	_convertAlertModel(alertModel) {
		return [
			alertModel.getDescription(),
			{
				buttons: alertModel
					.getActions()
					.map(({ label, href, dataTest }) =>
						text(
							{ emphasize: true },
							button({ link: true, href, dataTest }, label),
						),
					),
			},
		];
	}
}

const notifier = new Notifier();

/**
 * Extend base widgets with notifications.
 */
widget.extend((_that, my) => {
	my.success = (...rest) => notifier.success(...rest);
	my.warning = (...rest) => notifier.warning(...rest);
	my.error = (...rest) => notifier.error(...rest);
});

export default notifier;
